import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { GridStack } from 'gridstack';
import 'gridstack/dist/gridstack.min.css';
import './DashboardGrid.scss';

const DashboardGrid = ({ layout, onPositionChange, children }) => {
  const gridRef = useRef(null);
  const gridInstance = useRef(null);

  // Inicializar GridStack
  useEffect(() => {
    if (!gridInstance.current && gridRef.current) {
      gridInstance.current = GridStack.init(
        {
          column: 12,
          cellHeight: 80,
          margin: 10,
          float: true,
          animate: true,
          disableOneColumnMode: true,
          acceptWidgets: true,
          alwaysShowResizeHandle: false,
          resizable: {
            handles: 'e, se, s, sw, w',
          },
          draggable: {
            handle: '.ant-card-head',
            scroll: true,
          },
        },
        gridRef.current
      );

      // Evento cuando se mueve o redimensiona un widget
      gridInstance.current.on('change', (event, items) => {
        if (!items || items.length === 0) return;
        
        const widgets = items.map((item) => {
          const widgetId = item.id || item.el?.id;
          
          return {
            widgetId,
            x: item.x,
            y: item.y,
            w: item.w,
            h: item.h,
          };
        }).filter(w => w.widgetId);

        if (widgets.length > 0) {
          onPositionChange(widgets);
        }
      });
    }

    return () => {
      if (gridInstance.current) {
        gridInstance.current.destroy(false);
        gridInstance.current = null;
      }
    };
  }, [onPositionChange]);

  // Actualizar grid cuando cambian los widgets
  useEffect(() => {
    if (!gridInstance.current || !layout?.widgets) return;
    
    // Función para sincronizar widgets
    const syncWidgets = () => {
      if (!gridInstance.current) return;
      
      // Obtener nodos directamente del engine de GridStack
      const gridNodes = gridInstance.current.engine.nodes || [];
      
      // GridStack usa la propiedad 'el' para el elemento DOM
      const nodesMap = new Map(gridNodes.map(node => {
        const id = node.el?.id || node.id;
        return [id, node];
      }));
      
      // Procesar cada widget del layout
      layout.widgets.forEach((widget) => {
        const element = document.getElementById(widget.widgetId);
        
        if (!element) return;

        const currentNode = nodesMap.get(widget.widgetId);
        
        if (currentNode) {
          // Comparar posiciones (considerar valores undefined/null como 0)
          const currentX = currentNode.x ?? 0;
          const currentY = currentNode.y ?? 0;
          const currentW = currentNode.w ?? 0;
          const currentH = currentNode.h ?? 0;
          
          const targetX = widget.x ?? 0;
          const targetY = widget.y ?? 0;
          const targetW = widget.w ?? 0;
          const targetH = widget.h ?? 0;
          
          const isDifferent = 
            currentX !== targetX || 
            currentY !== targetY || 
            currentW !== targetW || 
            currentH !== targetH;
          
          if (isDifferent) {
            gridInstance.current.update(element, {
              x: targetX,
              y: targetY,
              w: targetW,
              h: targetH,
            });
          }
        } else {
          // Nodo no existe en GridStack, agregarlo
          gridInstance.current.makeWidget(element);
        }
      });

      // Remover widgets que ya no están en el layout
      const widgetIdsInLayout = new Set(layout.widgets.map(w => w.widgetId));
      gridNodes.forEach(node => {
        const nodeId = node.el?.id || node.id;
        if (!widgetIdsInLayout.has(nodeId)) {
          const element = document.getElementById(nodeId);
          if (element) {
            gridInstance.current.removeWidget(element, false);
          }
        }
      });
    };
    
    // Ejecutar sincronización
    const allElementsExist = layout.widgets.every(w => document.getElementById(w.widgetId));
    
    if (allElementsExist) {
      syncWidgets();
    } else {
      requestAnimationFrame(syncWidgets);
    }
    
  }, [layout]);

  return (
    <div className="dashboard-grid-container">
      <div ref={gridRef} className="grid-stack">
        {layout?.widgets?.map((widget) => (
          <div
            key={widget.widgetId}
            id={widget.widgetId}
            className="grid-stack-item"
            data-gs-id={widget.widgetId}
            data-gs-x={widget.x}
            data-gs-y={widget.y}
            data-gs-w={widget.w}
            data-gs-h={widget.h}
          >
            <div className="grid-stack-item-content">
              {children(widget)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

DashboardGrid.propTypes = {
  layout: PropTypes.shape({
    widgets: PropTypes.arrayOf(
      PropTypes.shape({
        widgetId: PropTypes.string.isRequired,
        x: PropTypes.number.isRequired,
        y: PropTypes.number.isRequired,
        w: PropTypes.number.isRequired,
        h: PropTypes.number.isRequired,
      })
    ),
  }),
  onPositionChange: PropTypes.func.isRequired,
  children: PropTypes.func.isRequired,
};

DashboardGrid.defaultProps = {
  layout: { widgets: [] },
};

export default DashboardGrid;
